import ActionButton from '@repo/ui/action-button'
import { DeleteIcon, PlusIcon, TravelIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { assignTripGroupingsToTrips } from '../../../api/trips/assign-trip-groupings-to-trips'
import { unassignTripGroupingsFromTrip } from '../../../api/trips/unassign-trip-groupings-from-trip'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  TripGrouping,
  TripGroupingFilter
} from '../../../lib/types/models/trip-grouping/trip-grouping'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { TABLE_ACTION_BUTTONS_CLASSNAME } from '../../../utils/constants/table-buttons-class.constants'
import { getTripGroupingColumns } from '../../../views/trip-groupings/utils'
import DynamicDataTable, { DynamicTableRef } from '../../elements/dynamic-table'
import SearchableDataTable from '../../elements/searchable-data-table'

type Props = {
  tripId: number
}

const TripGroupingAssignmentDataTable: React.FC<Props> = ({ tripId }) => {
  const tableRef = React.useRef<DynamicTableRef<TripGrouping>>(null)
  const [selectedTripGroupings, setSelectedTripGroupings] = useState<TripGrouping[]>([])

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'assign' | 'delete',
    TripGrouping
  >(['assign', 'delete'])

  const {
    mutate: assignTripGroupingsToTripsMutation,
    isPending: isAssignTripGroupingsToTripsPending
  } = useMutation({
    mutationFn: assignTripGroupingsToTrips,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('assign')
      toast.success('Groupe de voyage ajouté avec succès')
    }
  })

  const {
    mutate: unassignTripGroupingsFromTripMutation,
    isPending: isUnassignTripGroupingsFromTripPending
  } = useMutation({
    mutationFn: unassignTripGroupingsFromTrip,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('delete')
      toast.success('Groupe de voyage supprimé avec succès')
    }
  })

  return (
    <div>
      <DynamicDataTable<TripGrouping, TripGroupingFilter>
        ref={tableRef}
        title={'Appartient à'}
        columns={[
          ...getTripGroupingColumns('BASIC'),
          {
            accessorKey: 'id',
            header: '',
            cell: ({ row }) => (
              <div className={TABLE_ACTION_BUTTONS_CLASSNAME}>
                <ActionButton
                  variant={'danger'}
                  icon={DeleteIcon}
                  onClick={() => toggleModal('delete', row.original)}
                />
              </div>
            )
          }
        ]}
        permissions={{ delete: false }}
        onDataChange={setSelectedTripGroupings}
        filters={{ tripId }}
        endpoint={API.tripGroupings.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter à un nouveau groupe',
            fn: () => toggleModal('assign')
          }
        ]}
      />

      <Modal
        isOpen={modalState.assign}
        title={'Ajouter à des groupes de voyage'}
        icon={TravelIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('assign')}
      >
        <SearchableDataTable<TripGrouping, null>
          className={MODAL_BODY_CLASSNAME}
          columns={getTripGroupingColumns('SUMMARY')}
          selectedRows={selectedTripGroupings}
          description={`Vous pouvez choisir plus d'un groupe de voyages:`}
          endpoint={API.tripGroupings.getAll.url}
          onCancel={() => closeModel('assign')}
          onSelect={(tripGroupings) => {
            assignTripGroupingsToTripsMutation({
              tripIds: [tripId],
              tripGroupingIds: tripGroupings.map((tg) => tg.id)
            })
          }}
          confirmationText="Ajouter"
          isLoading={isAssignTripGroupingsToTripsPending}
        />
      </Modal>

      {selectedData && (
        <Modal
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            onConfirm: () => {
              unassignTripGroupingsFromTripMutation({
                tripId: tripId,
                tripGroupingIds: [selectedData.id]
              })
            }
          }}
          onCancel={() => closeModel('delete')}
          isOpen={modalState.delete}
          title={'Etes-vous sûr de vouloir détacher ce groupe?'}
          icon={DeleteIcon}
          isLoading={isUnassignTripGroupingsFromTripPending}
          variant={'destructive'}
        />
      )}
    </div>
  )
}

export default TripGroupingAssignmentDataTable
